import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { useSiteContext } from "../../contexts/SiteProvider";
import { showAlertAutoClose } from "../../services/notifyService";
import { get, post } from "../../services/smartApiService";
import { COMPANY_URLS, SECTOR_URLS } from "../../api/ManageUrls";
import { SdsForm } from "soft_digi";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";

/* -------------------- TYPES -------------------- */

type EntityType = "company" | "index";
type StatusType = "active" | "delisted" | "suspended";

interface SelectOption<T> {
  label: string;
  value: T;
}

/* -------------------- VALIDATION -------------------- */

const schema = Yup.object({
  entity_type: Yup.object({
    label: Yup.string().required(),
    value: Yup.mixed<EntityType>().oneOf(["company", "index"]).required(),
  }).required(),

  name: Yup.string().required("Name is required"),
  symbol: Yup.string().required("Symbol is required"),
  exchange: Yup.string().nullable(),
  isin: Yup.string().nullable(),
  description: Yup.string().nullable(),

  sector_id: Yup.object()
    .nullable()
    .when("entity_type", {
      is: (v: any) => v?.value === "company",
      then: (s) => s.required("Sector is required"),
    }),

  market_cap_category: Yup.object().nullable(),

  is_active: Yup.object({
    label: Yup.string().required(),
    value: Yup.mixed<StatusType>()
      .oneOf(["active", "delisted", "suspended"])
      .required(),
  }).required(),
});

/* -------------------- COMPONENT -------------------- */

interface CompanyFormProps {
  loadTableData: () => void;
  data?: any;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  loadTableData,
  data,
}) => {
  const { closeModal } = useSiteContext();
  const [sectors, setSectors] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const entityType = watch("entity_type")?.value;

  /* -------------------- LOAD SECTORS -------------------- */

  useEffect(() => {
    const sub = get(SECTOR_URLS.GET_ALL).subscribe((res) => {
      setSectors(res?.data || []);
    });
    return () => sub.unsubscribe();
  }, []);

  /* -------------------- EDIT AUTO MAPPING -------------------- */

  useEffect(() => {
    if (!data) return;

    reset({
      ...data,

      entity_type: {
        label: data.entity_type === "company" ? "Company" : "Index",
        value: data.entity_type,
      },

      is_active: {
        label:
          data.is_active.charAt(0).toUpperCase() +
          data.is_active.slice(1),
        value: data.is_active,
      },

      sector_id: data.sector
        ? { label: data.sector.name, value: data.sector.id }
        : null,

      market_cap_category: data.market_cap_category
        ? {
            label:
              data.market_cap_category.replace("_", " ").toLowerCase(),
            value: data.market_cap_category,
          }
        : null,
    });
  }, [data, reset]);

  /* -------------------- SUBMIT -------------------- */

  const onSubmit = (formData: any) => {
    const payload = {
      ...formData,

      entity_type: formData.entity_type?.value,
      is_active: formData.is_active?.value,

      // sector_id:
      //   formData.entity_type?.value === "company"
      //     ? formData.sector_id?.value ?? null
      //     : null,

      market_cap_category:
        formData.market_cap_category?.value ?? null,
    };

    const request$ = data?.id
      ? post(`${COMPANY_URLS.UPDATE}${data.id}`, payload)
      : post(COMPANY_URLS.INSERT, payload);

    request$.subscribe(
      () => {
        showAlertAutoClose("Saved successfully", "success");
        closeModal();
        loadTableData();
      },
      (err) =>
        showAlertAutoClose(
          err?.error?.message || "Save failed",
          "error"
        )
    );
  };

  /* -------------------- FORM ELEMENTS -------------------- */

  const companyElements: SdsFormElementProps[] = [
    {
      type: "SELECT_BOX",
      width: 4,
      name: "entity_type",
      element: {
        label: "Entity Type",
        options: [
          { label: "Company", value: "company" },
          { label: "Index", value: "index" },
        ],
        inputType: "BORDER_LABEL",
      },
    },

    { type: "TEXT_BOX", width: 4, name: "name", element: { label: "Name", inputType: "BORDER_LABEL" } },
    { type: "TEXT_BOX", width: 4, name: "symbol", element: { label: "Symbol", inputType: "BORDER_LABEL" } },
    { type: "TEXT_BOX", width: 4, name: "exchange", element: { label: "Exchange", inputType: "BORDER_LABEL" } },
    { type: "TEXT_BOX", width: 4, name: "isin", element: { label: "ISIN", inputType: "BORDER_LABEL" } },

    {
      type: "SELECT_BOX",
      width: 4,
      name: "sector_id",
      element: {
        label: "Sector",
        options: sectors.map((s) => ({ label: s.name, value: s.id })),
        inputType: "BORDER_LABEL",
      },
     // hideFunction: () => entityType !== "company",
    },

    {
      type: "SELECT_BOX",
      width: 4,
      name: "market_cap_category",
      element: {
        label: "Market Cap Category",
        options: [
          { label: "Large Cap", value: "LARGE_CAP" },
          { label: "Mid Cap", value: "MID_CAP" },
          { label: "Small Cap", value: "SMALL_CAP" },
        ],
        inputType: "BORDER_LABEL",
      },
      hideFunction: () => entityType !== "company",
    },

    {
      type: "SELECT_BOX",
      width: 4,
      name: "is_active",
      element: {
        label: "Status",
        options: [
          { label: "Active", value: "active" },
          { label: "Delisted", value: "delisted" },
          { label: "Suspended", value: "suspended" },
        ],
        inputType: "BORDER_LABEL",
      },
    },

    {
      type: "BUTTON",
      width: 12,
      name: "submit",
      element: {
        label: data ? "Update" : "Save",
        classList: ["button", "is-primary"],
        onClick: handleSubmit(onSubmit),
      },
    },
  ];

  return (
    <div className="box">
      <SdsForm elements={companyElements} control={control} errors={errors} />
    </div>
  );
};

export default CompanyForm;
